import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Plus, Pencil, Trash2, X, CheckSquare, Square } from 'lucide-react';
import { formatQuantity, formatCost, formatCostPerUnit } from '../utils/formatters';

const IngredientsPage = () => {
  const { isAdmin } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    supplier: '',
    cost: '',
    unit_size: '',
    unit_type: 'g'
  });

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    supplier: '',
    cost: '',
    unit_size: '',
    unit_type: ''
  });

  const fetchIngredients = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (supplierFilter && supplierFilter !== 'all') params.supplier = supplierFilter;
      
      const response = await axios.get(`${API}/ingredients`, { params });
      setIngredients(response.data);
    } catch (error) {
      toast.error('Failed to fetch ingredients');
    } finally {
      setLoading(false);
    }
  }, [search, supplierFilter]);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/ingredients/suppliers/list`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  };

  useEffect(() => {
    fetchIngredients();
    fetchSuppliers();
  }, [fetchIngredients]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds([]);
  }, [search, supplierFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cost: parseFloat(formData.cost),
        unit_size: parseFloat(formData.unit_size)
      };

      if (editingIngredient) {
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, payload);
        toast.success('Ingredient updated');
      } else {
        await axios.post(`${API}/ingredients`, payload);
        toast.success('Ingredient created');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchIngredients();
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to save ingredient');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ingredient?')) return;
    try {
      await axios.delete(`${API}/ingredients/${id}`);
      toast.success('Ingredient deleted');
      setSelectedIds(prev => prev.filter(i => i !== id));
      fetchIngredients();
    } catch (error) {
      toast.error('Failed to delete ingredient');
    }
  };

  const openEditDialog = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      name: ingredient.name,
      supplier: ingredient.supplier,
      cost: ingredient.cost.toString(),
      unit_size: ingredient.unit_size.toString(),
      unit_type: ingredient.unit_type
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingIngredient(null);
    setFormData({
      name: '',
      supplier: '',
      cost: '',
      unit_size: '',
      unit_type: 'g'
    });
  };

  // Multi-select handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === ingredients.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(ingredients.map(i => i.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const isAllSelected = ingredients.length > 0 && selectedIds.length === ingredients.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < ingredients.length;

  // Bulk edit handlers
  const openBulkEdit = () => {
    setBulkFormData({ supplier: '', cost: '', unit_size: '', unit_type: '' });
    setIsBulkEditOpen(true);
  };

  const handleBulkUpdate = async (e) => {
    e.preventDefault();
    try {
      const updates = {};
      if (bulkFormData.supplier) updates.supplier = bulkFormData.supplier;
      if (bulkFormData.cost) updates.cost = parseFloat(bulkFormData.cost);
      if (bulkFormData.unit_size) updates.unit_size = parseFloat(bulkFormData.unit_size);
      if (bulkFormData.unit_type) updates.unit_type = bulkFormData.unit_type;

      if (Object.keys(updates).length === 0) {
        toast.error('Please fill at least one field to update');
        return;
      }

      const response = await axios.put(`${API}/ingredients/bulk-update`, {
        ingredient_ids: selectedIds,
        updates
      });

      toast.success(`Updated ${response.data.updated_count} ingredients`);
      setIsBulkEditOpen(false);
      setSelectedIds([]);
      fetchIngredients();
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to update ingredients');
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="ingredients-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>
          Raw Ingredients
        </h1>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="bg-zinc-950 text-white hover:bg-zinc-800 px-6 py-2 font-medium tracking-wide"
            data-testid="add-ingredient-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Ingredient
          </Button>
        )}
      </div>

      {/* Bulk Edit Toolbar */}
      {isAdmin && selectedIds.length > 0 && (
        <div className="mb-4 p-3 bg-zinc-900 text-white flex items-center justify-between" data-testid="bulk-edit-toolbar">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5" />
            <span className="font-medium">{selectedIds.length} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openBulkEdit}
              variant="secondary"
              size="sm"
              className="bg-white text-zinc-900 hover:bg-zinc-100"
              data-testid="bulk-edit-btn"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit Selected
            </Button>
            <Button
              onClick={() => setSelectedIds([])}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-zinc-800"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search ingredients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-zinc-300 rounded-none"
            data-testid="ingredient-search-input"
          />
        </div>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger className="w-full sm:w-[200px] border-zinc-300 rounded-none" data-testid="supplier-filter">
            <SelectValue placeholder="All Suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Suppliers</SelectItem>
            {suppliers.map((supplier) => (
              <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="progress-bar mb-4">
          <div className="progress-bar-fill"></div>
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="ingredients-table">
          <thead>
            <tr>
              {isAdmin && (
                <th className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="border-zinc-400"
                    data-testid="select-all-checkbox"
                  />
                </th>
              )}
              <th>Ingredient Name</th>
              <th>Supplier</th>
              <th>Pack Size</th>
              <th>Unit</th>
              {isAdmin && <th>Pack Cost</th>}
              {isAdmin && <th>Cost/g</th>}
              {isAdmin && <th>Cost/100g</th>}
              {isAdmin && <th className="w-24">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {ingredients.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 8 : 5} className="text-center py-12 text-zinc-500">
                  No ingredients found. {isAdmin && 'Add your first ingredient or import from CSV.'}
                </td>
              </tr>
            ) : (
              ingredients.map((ingredient) => (
                <tr 
                  key={ingredient.id} 
                  data-testid={`ingredient-row-${ingredient.id}`}
                  className={selectedIds.includes(ingredient.id) ? 'bg-zinc-100' : ''}
                >
                  {isAdmin && (
                    <td>
                      <Checkbox
                        checked={selectedIds.includes(ingredient.id)}
                        onCheckedChange={() => toggleSelect(ingredient.id)}
                        className="border-zinc-400"
                        data-testid={`select-ingredient-${ingredient.id}`}
                      />
                    </td>
                  )}
                  <td className="font-medium">{ingredient.name}</td>
                  <td>
                    <span className="px-2 py-1 bg-zinc-100 text-xs font-medium">
                      {ingredient.supplier}
                    </span>
                  </td>
                  <td className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
                    {formatQuantity(ingredient.unit_size)}
                  </td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-medium ${
                      ingredient.unit_type === 'pieces' ? 'bg-amber-50 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                    }`}>
                      {ingredient.unit_type}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
                      {formatCost(ingredient.cost)}
                    </td>
                  )}
                  {isAdmin && (
                    <td className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
                      {formatCostPerUnit(ingredient.cost_per_unit)}/{ingredient.unit_type === 'pieces' ? 'pc' : 'g'}
                    </td>
                  )}
                  {isAdmin && (
                    <td className="tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
                      {ingredient.unit_type === 'pieces' 
                        ? '-' 
                        : `£${(ingredient.cost_per_unit * 100).toFixed(2)}`}
                    </td>
                  )}
                  {isAdmin && (
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditDialog(ingredient)}
                          className="p-1.5 border border-zinc-200 hover:bg-zinc-100 transition-colors"
                          data-testid={`edit-ingredient-${ingredient.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(ingredient.id)}
                          className="p-1.5 border border-zinc-200 hover:bg-red-50 hover:border-red-200 text-red-600 transition-colors"
                          data-testid={`delete-ingredient-${ingredient.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Total Count */}
      <div className="mt-4 text-sm text-zinc-500">
        Showing {ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''}
        {selectedIds.length > 0 && ` • ${selectedIds.length} selected`}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editingIngredient ? 'Edit Ingredient' : 'Add New Ingredient'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Ingredient Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Mild Grated Cheddar Cheese"
                  className="rounded-none border-zinc-300"
                  required
                  data-testid="ingredient-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="supplier">Supplier</Label>
                <Input
                  id="supplier"
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  placeholder="e.g., BidFood"
                  className="rounded-none border-zinc-300"
                  required
                  data-testid="ingredient-supplier-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cost">Pack Cost (£)</Label>
                  <Input
                    id="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    placeholder="0.00"
                    className="rounded-none border-zinc-300 tabular-nums"
                    style={{ fontFamily: 'var(--font-mono)' }}
                    required
                    data-testid="ingredient-cost-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="unit_size">Pack Size</Label>
                  <Input
                    id="unit_size"
                    type="number"
                    step="0.01"
                    value={formData.unit_size}
                    onChange={(e) => setFormData({ ...formData, unit_size: e.target.value })}
                    placeholder="e.g., 1000"
                    className="rounded-none border-zinc-300 tabular-nums"
                    style={{ fontFamily: 'var(--font-mono)' }}
                    required
                    data-testid="ingredient-unit-size-input"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="unit_type">Unit Type</Label>
                <Select value={formData.unit_type} onValueChange={(value) => setFormData({ ...formData, unit_type: value })}>
                  <SelectTrigger className="rounded-none border-zinc-300" data-testid="ingredient-unit-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="g">Grams (g)</SelectItem>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="ml">Millilitres (ml)</SelectItem>
                    <SelectItem value="L">Litres (L)</SelectItem>
                    <SelectItem value="pieces">Pieces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-none border-zinc-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800"
                data-testid="save-ingredient-btn"
              >
                {editingIngredient ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              Edit {selectedIds.length} Ingredients
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkUpdate}>
            <div className="grid gap-4 py-4">
              <p className="text-sm text-zinc-500">
                Leave fields empty to keep existing values. Only filled fields will be updated.
              </p>
              
              <div className="grid gap-2">
                <Label htmlFor="bulk_supplier">Supplier</Label>
                <Input
                  id="bulk_supplier"
                  value={bulkFormData.supplier}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, supplier: e.target.value })}
                  placeholder="Leave empty to keep current"
                  className="rounded-none border-zinc-300"
                  data-testid="bulk-supplier-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="bulk_cost">Pack Cost (£)</Label>
                  <Input
                    id="bulk_cost"
                    type="number"
                    step="0.01"
                    value={bulkFormData.cost}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, cost: e.target.value })}
                    placeholder="Leave empty"
                    className="rounded-none border-zinc-300 tabular-nums"
                    style={{ fontFamily: 'var(--font-mono)' }}
                    data-testid="bulk-cost-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bulk_unit_size">Pack Size</Label>
                  <Input
                    id="bulk_unit_size"
                    type="number"
                    step="0.01"
                    value={bulkFormData.unit_size}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, unit_size: e.target.value })}
                    placeholder="Leave empty"
                    className="rounded-none border-zinc-300 tabular-nums"
                    style={{ fontFamily: 'var(--font-mono)' }}
                    data-testid="bulk-unit-size-input"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="bulk_unit_type">Unit Type</Label>
                <Select 
                  value={bulkFormData.unit_type} 
                  onValueChange={(value) => setBulkFormData({ ...bulkFormData, unit_type: value })}
                >
                  <SelectTrigger className="rounded-none border-zinc-300" data-testid="bulk-unit-type-select">
                    <SelectValue placeholder="Leave unchanged" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="g">Grams (g)</SelectItem>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="ml">Millilitres (ml)</SelectItem>
                    <SelectItem value="L">Litres (L)</SelectItem>
                    <SelectItem value="pieces">Pieces</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsBulkEditOpen(false)}
                className="rounded-none border-zinc-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800"
                data-testid="bulk-save-btn"
              >
                Update {selectedIds.length} Items
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IngredientsPage;
